import React, { useEffect, useState } from 'react'
import { motion } from 'motion/react'

import { getChatProfile, putChatProfile, type HgProfile } from '../../api/client'

const OPTIONS: { value: HgProfile; label: string }[] = [
    { value: 'fast', label: 'Fast' },
    { value: 'balanced', label: 'Balanced' },
    { value: 'thorough', label: 'Thorough' },
]

/**
 * The user's default Chats profile (`users.prefs.chat_profile`). Admin-only
 * until the profiles route to different models; the page decides that.
 */
const ProfilePicker: React.FC = () => {
    const [profile, setProfile] = useState<HgProfile | null>(null)

    useEffect(() => {
        let alive = true
        getChatProfile()
            .then((p) => alive && setProfile(p))
            .catch(() => alive && setProfile('balanced'))
        return () => {
            alive = false
        }
    }, [])

    const choose = async (next: HgProfile) => {
        if (next === profile) return
        const previous = profile
        setProfile(next)
        try {
            setProfile(await putChatProfile(next))
        } catch {
            setProfile(previous)
        }
    }

    return (
        <div
            role="radiogroup"
            aria-label="Profile"
            className="inline-flex rounded-lg bg-white/70 p-0.5 text-sm"
            data-testid="chat-profile-picker"
        >
            {OPTIONS.map((option) => {
                const selected = option.value === profile
                return (
                    <motion.button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => void choose(option.value)}
                        whileTap={{ scale: 0.9 }}
                        transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        className={`px-2.5 py-1 rounded-md cursor-pointer ${
                            selected ? 'bg-action text-plum font-medium' : 'text-gray-600 hover:text-gray-900'
                        }`}
                    >
                        {option.label}
                    </motion.button>
                )
            })}
        </div>
    )
}

export default ProfilePicker
